import React from 'react';
import { PrintJob, PrintProfileId } from './types';
import { PartOrder } from '../../app/models/PartOrder';
import { Workstation } from '../../app/containers/OrderDetails/types';
import { CachedInstructionMapping } from './instructionsTypes';
import {
  getPrintProfileDefinition,
  NOTICE_BOOKLET_PROFILE_ID,
} from '../../../shared/printing/printProfiles';

export interface PrintJobFilters {
  includePicking?: boolean;
  includeLabels?: boolean;
  includeCarrierPages?: boolean;
  includeNotices?: boolean;
}

const PROFILE_A4_SIMPLE: PrintProfileId = 'A4_SIMPLE';

export class PrintJobFactory {
  private static uniqueCounter = 0;

  private static buildUniqueId(...segments: Array<string | number>): string {
    const baseId = segments.join('-');
    const uniqueSuffix = `${Date.now()}-${PrintJobFactory.uniqueCounter++}`;
    return `${baseId}-${uniqueSuffix}`;
  }

  static createPickingJob(partOrder: PartOrder): PrintJob {
    const relevantWorkstations = this.getRelevantWorkstations(partOrder);
    const estimatedPages = relevantWorkstations
      .map(ws => this.estimatePickingPages(partOrder, ws))
      .reduce((total, count) => total + count, 0);

    return {
      id: this.buildUniqueId('picking', partOrder.id),
      type: 'picking',
      workstations: relevantWorkstations,
      partOrderId: partOrder.id,
      content: React.createElement(
        React.Fragment,
        null,
        relevantWorkstations.map(ws =>
          React.createElement('div', { key: `picking-${ws}` }, `Picking content for ${Workstation[ws]}`),
        ),
      ),
      metadata: {
        pageCount: Math.max(1, estimatedPages),
        groupKey: 'picking',
        format: 'A4',
        profileId: PROFILE_A4_SIMPLE,
        originalPartOrder: partOrder,
      },
    };
  }

  static createLabelJob(partOrder: PartOrder): PrintJob | null {
    const packages = this.collectPackages(partOrder);
    if (packages.length === 0) {
      return null;
    }

    return {
      id: this.buildUniqueId('label', partOrder.id),
      type: 'label',
      partOrderId: partOrder.id,
      content: React.createElement(
        React.Fragment,
        null,
        packages.map(pkg =>
          React.createElement('div', { key: `label-${pkg.id}` }, `Label for package ${pkg.id}`),
        ),
      ),
      metadata: {
        pageCount: packages.length,
        groupKey: 'label',
        format: 'A4',
        profileId: PROFILE_A4_SIMPLE,
        originalPartOrder: partOrder,
      },
    };
  }

  static createCarrierPageJob(partOrder: PartOrder): PrintJob | null {
    if (!partOrder.carrier || partOrder.carrier?.name === 'BPOST') {
      return null;
    }

    return {
      id: this.buildUniqueId('carrier-page', partOrder.id),
      type: 'carrier-page',
      partOrderId: partOrder.id,
      content: React.createElement('div', null, 'Carrier page content'),
      metadata: {
        pageCount: 1,
        groupKey: 'carrier-page',
        format: 'A4',
        profileId: PROFILE_A4_SIMPLE,
        originalPartOrder: partOrder,
      },
    };
  }

  static createAssemblyJob(partOrder: PartOrder, productId: number, electronPath?: string): PrintJob {
    const profile = getPrintProfileDefinition(NOTICE_BOOKLET_PROFILE_ID);

    return {
      id: this.buildUniqueId('assembly', partOrder.id, productId),
      type: 'assembly',
      partOrderId: partOrder.id,
      content: React.createElement('div', null, `Assembly Instructions for product ${productId}`),
      metadata: {
        pageCount: 4,
        groupKey: 'assembly',
        format: profile.sheetFormat,
        // Saddle-stitch booklets must request folding so the finisher completes the booklet
        requiresFolding: true,
        requiresStapling: true,
        profileId: NOTICE_BOOKLET_PROFILE_ID,
        originalPartOrder: partOrder,
        productId,
      },
      electronPath,
    };
  }

  static createJobsForPartOrder(partOrder: PartOrder, includeAssemblyInstructions: boolean = false): PrintJob[] {
    const jobs: PrintJob[] = [];

    // Add carrier page if needed
    const carrierPageJob = this.createCarrierPageJob(partOrder);
    if (carrierPageJob) {
      jobs.push(carrierPageJob);
    }

    // Add picking job covering all relevant workstations
    jobs.push(this.createPickingJob(partOrder));

    // Add label job (one PDF per part order)
    const labelJob = this.createLabelJob(partOrder);
    if (labelJob) {
      jobs.push(labelJob);
    }

    // Add assembly instruction jobs if requested
    if (includeAssemblyInstructions && partOrder.products) {
      for (const product of partOrder.products) {
        const hasInstructions = (product as any).instructions || (product as any).assemblyInstructions;
        if (hasInstructions) {
          jobs.push(this.createAssemblyJob(partOrder, product.id));
        }
      }
    }

    return jobs;
  }

  static attachAssemblyPaths(jobs: PrintJob[], urlMapping: CachedInstructionMapping): void {
    for (const job of jobs) {
      if (job.type !== 'assembly') {
        continue;
      }

      const productId = job.metadata.productId ?? this.extractProductIdFromJobId(job.id);
      if (!productId) {
        continue;
      }

      const product = job.metadata.originalPartOrder.products?.find(p => p.id === productId);
      if (!product) {
        continue;
      }

      const instructionUrl = (product as any).instructions ?? (product as any).assemblyInstructions;
      if (instructionUrl && urlMapping[instructionUrl]) {
        job.electronPath = urlMapping[instructionUrl];
      }
    }
  }

  static createJobsForPartOrderWithFilters(
    partOrder: PartOrder,
    filters: PrintJobFilters,
  ): PrintJob[] {
    const {
      includePicking = true,
      includeLabels = true,
      includeCarrierPages = true,
      includeNotices = false,
    } = filters;

    const baseJobs = this.createJobsForPartOrder(partOrder, includeNotices);

    return baseJobs.filter(job => {
      if (job.type === 'picking') return includePicking;
      if (job.type === 'label' || job.type === 'carrier-page') {
        if (job.type === 'label') return includeLabels;
        return includeCarrierPages;
      }
      if (job.type === 'assembly') return includeNotices;
      return true;
    });
  }

  private static estimatePickingPages(partOrder: PartOrder, workstation: Workstation): number {
    if (!partOrder.trolleys) return 1;
    
    const relevantTrolleys = partOrder.trolleys.filter(trolley => 
      this.isWorkstationRelevant(trolley, workstation)
    );
    
    return Math.max(1, Math.ceil(relevantTrolleys.length / 2));
  }

  private static getRelevantWorkstations(partOrder: PartOrder): Workstation[] {
    const workstations: Workstation[] = [];
    
    if (partOrder.progressionCabinet && partOrder.progressionCabinet.progress < 100) {
      workstations.push(Workstation.Cabinet);
    }
    if (partOrder.progressionCorners && partOrder.progressionCorners.progress < 100) {
      workstations.push(Workstation.Corner);
    }
    if (partOrder.progressionDrawers && partOrder.progressionDrawers.progress < 100) {
      workstations.push(Workstation.Drawer);
    }
    if (partOrder.progressionAccessories && partOrder.progressionAccessories.progress < 100) {
      workstations.push(Workstation.Accessory);
    }
    
    return workstations.length > 0 ? workstations : [Workstation.Cabinet];
  }

  private static collectPackages(partOrder: PartOrder): Array<{ id: number }> {
    if (!partOrder.trolleys || partOrder.trolleys.length === 0) {
      return [];
    }

    const packages: Array<{ id: number }> = [];

    for (const trolley of partOrder.trolleys) {
      if (!trolley?.packages) {
        continue;
      }
      for (const pkg of trolley.packages) {
        if (pkg && typeof pkg.id === 'number') {
          packages.push({ id: pkg.id });
        }
      }
    }

    return packages;
  }

  private static isWorkstationRelevant(trolley: any, workstation: Workstation): boolean {
    // This logic should match the existing PrintContent component logic
    return trolley.production && trolley.production.length > 0;
  }

  private static extractProductIdFromJobId(jobId: string): number | null {
    const numericSegments = jobId
      .split('-')
      .filter(segment => /^[0-9]+$/.test(segment));

    if (numericSegments.length >= 2) {
      return Number(numericSegments[1]);
    }

    return null;
  }
}
